import type { UserInformation } from '@/shared/types/authentication'

const storagePrefix = 'mma_'

const userIdKey = 'userId'
const userInformationKey = storagePrefix + 'user_information'
const filterKey = storagePrefix + 'filter'
// The line preferences are kept per space, like the guides of the input area (`mma_camera_mic_onboarding_seen_<id>`):
// the key is the name followed by the id of the space
const preferredLinesKey = (spaceId: string) => `${storagePrefix}preferred_lines_${spaceId}`
const defaultLineKey = (spaceId: string) => `${storagePrefix}default_line_${spaceId}`
const availableLinesKey = (spaceId: string) => `${storagePrefix}available_lines_${spaceId}`
const selectedResolutionSourcesKey = storagePrefix + 'selected_resolution_sources'
const onboardingCompletedKey = (spaceId: string) => `${storagePrefix}onboarding_completed_${spaceId}`

export type LineOption = { value: string; label: string }

const storage = {
    getUserId: (): string | undefined => {
        const information = storage.getUserInformation()
        const userId = information && information[userIdKey]

        return userId
    },

    getUserInformation: (): UserInformation | undefined => {
        let information = undefined
        const rawInformation = window.localStorage.getItem(userInformationKey)

        if (rawInformation)
            information = JSON.parse(rawInformation)

        return information
    },

    getFilter: (): Record<string, unknown> | undefined => {
        let information = undefined
        const rawSelection = window.localStorage.getItem(filterKey)

        if (rawSelection)
            information = JSON.parse(rawSelection)

        return information
    },

    getColorTheme: (): boolean | undefined => {
        let information = undefined
        const rawInformation = window.localStorage.getItem(userInformationKey)

        if (rawInformation) {
            const parsedInformation = JSON.parse(rawInformation)
            information = parsedInformation.colorTheme
        }

        return information
    },

    setUserInformation: (source: UserInformation) => {
        const oldInformation = storage.getUserInformation()
        const newInformation = { ...oldInformation, ...source }
        const rawInformation = JSON.stringify(newInformation)

        window.localStorage.setItem(userInformationKey, rawInformation)
    },

    setFilter: (name: string, selection: unknown) => {
        const oldInformation = storage.getFilter()
        const newInformation = { ...oldInformation, [name]: selection }
        const rawInformation = JSON.stringify(newInformation)

        window.localStorage.setItem(filterKey, rawInformation)
    },

    setColorTheme: (isDark: boolean) => {
        const information = storage.getUserInformation() || {}
        information.colorTheme = isDark

        storage.setUserInformation(information)
    },

    clearUserInformation: () => {
        const information = storage.getUserInformation()
        const preserved = information?.colorTheme !== undefined
            ? { colorTheme: information.colorTheme }
            : {}
        window.localStorage.setItem(userInformationKey, JSON.stringify(preserved))
    },

    clearFilter: () => {
        window.localStorage.removeItem(filterKey)
    },

    // --- Preferred Lines of a space (localStorage — synced with PATCH /v1/preferences) ---

    getPreferredLines: (spaceId: string): LineOption[] => {
        try {
            const raw = window.localStorage.getItem(preferredLinesKey(spaceId))
            return raw ? (JSON.parse(raw) as LineOption[]) : []
        } catch {
            return []
        }
    },

    setPreferredLines: (lines: LineOption[], spaceId: string): void => {
        window.localStorage.setItem(preferredLinesKey(spaceId), JSON.stringify(lines))
        window.dispatchEvent(new CustomEvent('mma:preferredLinesChanged'))
    },

    // The default line of a space (`user.lines.default.<spaceId>`): null means "None", no line is pre-selected
    getDefaultLine: (spaceId: string): string | null => {
        return window.localStorage.getItem(defaultLineKey(spaceId)) || null
    },

    setDefaultLine: (line: string | null, spaceId: string): void => {
        if (line) window.localStorage.setItem(defaultLineKey(spaceId), line)
        else window.localStorage.removeItem(defaultLineKey(spaceId))
    },

    clearPreferredLines: (spaceId: string): void => {
        window.localStorage.removeItem(preferredLinesKey(spaceId))
        window.dispatchEvent(new CustomEvent('mma:preferredLinesChanged'))
    },

    getOnboardingCompleted: (spaceId: string): boolean => {
        return window.localStorage.getItem(onboardingCompletedKey(spaceId)) === '1'
    },

    setOnboardingCompleted: (completed: boolean, spaceId: string): void => {
        if (completed) {
            window.localStorage.setItem(onboardingCompletedKey(spaceId), '1')
        } else {
            window.localStorage.removeItem(onboardingCompletedKey(spaceId))
        }
    },

    // --- Available Lines cache of a space (localStorage — from the last loaded assistant of the space) ---

    getAvailableLines: (spaceId: string): LineOption[] => {
        try {
            const raw = window.localStorage.getItem(availableLinesKey(spaceId))
            return raw ? (JSON.parse(raw) as LineOption[]) : []
        } catch {
            return []
        }
    },

    setAvailableLines: (lines: LineOption[], spaceId: string): void => {
        window.localStorage.setItem(availableLinesKey(spaceId), JSON.stringify(lines))
    },

    getSelectedResolutionSources: (): string[] => {
        try {
            const raw = window.localStorage.getItem(selectedResolutionSourcesKey)
            return raw ? (JSON.parse(raw) as string[]) : []
        } catch {
            return []
        }
    },
    setSelectedResolutionSources: (sources: string[]): void => {
        window.localStorage.setItem(selectedResolutionSourcesKey, JSON.stringify(sources))
    },
    clearSelectedResolutionSources: (): void => {
        window.localStorage.removeItem(selectedResolutionSourcesKey)
    },
}

export default storage
